import { Button } from '@neo/index'
import { Checkbox } from '@neo/primitives/checkbox'
import { Input } from '@neo/primitives/input'
import { Switch } from '@neo/primitives/switch'
import type { ApiResponse } from '@shared/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useState } from 'react'
import { useFlowStore } from 'v2/pages/flow/store'
import { HANDOFF_CONTEXT_FIELDS } from 'v2/pages/settings/flows/queues/qnr.config'
import { resolveBaseUrl } from 'v2/shared/services/create-service'
import { handoffContextService } from 'v2/shared/services/handoff-context-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { conversationQueryKeys } from 'v2/shared/utils/query-key-management'
import type { HandoffApiKey } from 'v2/types/api/handoff-context'
import { errorToast, successToast } from 'v2/utils/toasts'
import { fieldLabel, integrationInstructions, uploadUrl } from './handoff-context.helpers'
import type { QNRFieldRendererProps, QNRFormValues } from './types'

type ContextPolicy = NonNullable<QNRFormValues['context']>
const DEFAULT_POLICY: ContextPolicy = {
  enabled: false,
  sources: ['external'],
  fields: [],
}

const adminError = (title: string, action: string) => (error: unknown) =>
  errorToast({
    title,
    description:
      (error as AxiosError).response?.status === 403
        ? `Only administrators can ${action}.`
        : getErrorText(error as AxiosError<ApiResponse<unknown>>),
  })

const copy = (text: string, what: string) =>
  navigator.clipboard.writeText(text).then(
    () => successToast({ title: `${what} copied` }),
    () => errorToast({ title: `${what} not copied`, description: 'Select and copy it manually.' }),
  )

/** Context section inside Assign to human: this node's policy plus the organization's API key.
 * A `basic` field gets no field subscription from FieldsRenderer, so the section subscribes itself. */
export const HandoffContext = ({ form }: Pick<QNRFieldRendererProps, 'form'>) => (
  <form.Subscribe selector={(state) => state.values.context}>
    {(context) => <ContextFields form={form} context={context || DEFAULT_POLICY} />}
  </form.Subscribe>
)

const ContextFields = ({
  form,
  context,
}: Pick<QNRFieldRendererProps, 'form'> & { context: ContextPolicy }) => {
  const update = (patch: Partial<ContextPolicy>) => {
    form.setFieldValue('context', { ...context, ...patch })
  }
  const toggleField = (field: (typeof HANDOFF_CONTEXT_FIELDS)[number], on: boolean) =>
    update({
      fields: on
        ? [...new Set([...context.fields, field])]
        : context.fields.filter((f) => f !== field),
    })
  const url = uploadUrl(resolveBaseUrl())

  return (
    <div className="flex flex-col gap-3" data-testid="qnr-context-section">
      <Switch
        id="context.enabled"
        name="context.enabled"
        value={context.enabled}
        onChange={(on) => update({ enabled: on, sources: ['external'] })}
        label="Include conversation context"
      />
      {context.enabled && (
        <>
          <div className="flex flex-col gap-1">
            <span className="text-xs-500 text-secondary">
              Information to show (all when none selected)
            </span>
            {HANDOFF_CONTEXT_FIELDS.map((field) => (
              <Checkbox
                key={field}
                id={`context.field.${field}`}
                name={`context.field.${field}`}
                label={fieldLabel(field)}
                value={context.fields.includes(field)}
                onChange={(on) => toggleField(field, on)}
              />
            ))}
          </div>
          <Connection url={url} />
          <details className="text-xs-400" data-testid="qnr-context-instructions">
            <summary className="cursor-pointer text-xs-500">How your AI sends notes</summary>
            <pre className="mt-2 whitespace-pre-wrap break-words rounded-md bg-bg2 p-2">
              {integrationInstructions(url)}
            </pre>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => copy(integrationInstructions(url), 'Integration instructions')}
            >
              Copy instructions
            </Button>
          </details>
          <span className="text-xs-400 text-secondary">
            Calls route immediately; context that arrives later appears automatically.
          </span>
        </>
      )}
    </div>
  )
}

/** The organization's one API key: the same for every Flow. Only admins can view or change it. */
const Connection = ({ url }: { url: string }) => {
  const isEditing = useFlowStore((state) => state.isEditing)
  const queryClient = useQueryClient()
  const { data: connection } = useQuery({
    queryKey: conversationQueryKeys().handoffContextConnection,
    queryFn: async () => (await handoffContextService.getConnection()).data,
    retry: false,
    staleTime: 60_000,
  })
  // undefined until fetched; null when the stored key predates viewable keys
  const [apiKey, setApiKey] = useState<string | null>()
  const [visible, setVisible] = useState(false)
  const [name, setName] = useState<string>()
  const [confirmRegenerate, setConfirmRegenerate] = useState(false)
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: conversationQueryKeys().handoffContextConnection })
  const keyLoaded = (key: HandoffApiKey) => {
    setApiKey(key.api_key)
    return refresh()
  }
  const reveal = useMutation({
    mutationFn: async () => (await handoffContextService.revealKey()).data,
    onSuccess: keyLoaded,
    onError: adminError('API key not shown', 'view the API key'),
  })
  const regenerate = useMutation({
    mutationFn: async () => (await handoffContextService.regenerateKey()).data,
    onSuccess: (key) => {
      setConfirmRegenerate(false)
      setVisible(true)
      successToast({ title: 'API key regenerated' })
      return keyLoaded(key)
    },
    onError: adminError('API key not regenerated', 'regenerate the API key'),
  })
  const rename = useMutation({
    mutationFn: async (value: string) =>
      (await handoffContextService.renameConnection(value)).data,
    onSuccess: () => {
      setName(undefined)
      return refresh()
    },
    onError: adminError('Name not saved', 'change the name'),
  })
  const withKey = (use: (key: string) => void) => {
    if (apiKey) return use(apiKey)
    if (apiKey === undefined)
      reveal.mutate(undefined, { onSuccess: (key) => key.api_key && use(key.api_key) })
  }
  if (!connection) return null
  const draftName = name ?? connection.name

  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-st2 bg-gray-50 p-3"
      data-testid="qnr-context-connection"
    >
      <span className="text-xs-500">
        Your AI&apos;s connection (shared by every Flow of this organization)
      </span>
      {connection.is_admin ? (
        <>
          <div className="flex items-end gap-2">
            <Input
              label="Name shown to agents"
              value={draftName}
              onChange={(e) => setName(e.target.value)}
            />
            {draftName.trim() && draftName.trim() !== connection.name && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!isEditing || rename.isPending}
                onClick={() => rename.mutate(draftName.trim())}
              >
                Save
              </Button>
            )}
          </div>
          <span className="text-xs-500">API key</span>
          <code className="break-all text-xs-400" data-testid="qnr-context-api-key">
            {visible && apiKey ? apiKey : '••••••••••••••••'}
          </code>
          {apiKey === null && (
            <span className="text-xs-400 text-secondary">
              This key was issued before keys could be viewed again. Regenerate it to view and copy
              it.
            </span>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={reveal.isPending || apiKey === null}
              onClick={() => (visible ? setVisible(false) : withKey(() => setVisible(true)))}
            >
              {visible ? 'Hide' : 'Show'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={reveal.isPending || apiKey === null}
              onClick={() => withKey((key) => copy(key, 'API key'))}
            >
              Copy
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!isEditing || regenerate.isPending}
              onClick={() => (confirmRegenerate ? regenerate.mutate() : setConfirmRegenerate(true))}
            >
              {confirmRegenerate ? 'Confirm: the current key stops working' : 'Regenerate'}
            </Button>
          </div>
        </>
      ) : (
        <>
          <span className="text-xs-400">Shown to agents as {connection.name}</span>
          <span className="text-xs-400 text-secondary">
            {connection.has_key
              ? 'Ask an admin for the API key.'
              : 'An admin needs to open this section once to create the API key.'}
          </span>
        </>
      )}
      <div className="text-xs-400 text-secondary break-all">Upload URL: {url}</div>
      <Button type="button" variant="ghost" size="sm" onClick={() => copy(url, 'Upload URL')}>
        Copy upload URL
      </Button>
    </div>
  )
}
